import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../services/api';
import useConfirm from '../../hooks/useConfirm';
import './Admin.css';

const MASK = '********';

const cellText = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
};

function AdminTables() {
  const [confirm, confirmDialog] = useConfirm();
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [rows, setRows] = useState([]);
  const [pkColumns, setPkColumns] = useState([]);
  const [editableColumns, setEditableColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [editRow, setEditRow] = useState(null);
  const [editPayload, setEditPayload] = useState('');

  const loadTables = useCallback(async () => {
    try {
      setError('');
      const res = await adminAPI.listTables();
      const tableList = res.data?.tables || [];
      setTables(tableList);
      setSelectedTable((current) => current || (tableList.length > 0 ? tableList[0].name : ''));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load tables');
    }
  }, []);

  const loadRows = useCallback(async () => {
    if (!selectedTable) return;
    try {
      setLoading(true);
      setError('');
      const res = await adminAPI.getRows(selectedTable, { limit, offset });
      setRows(res.data?.rows || []);
      setTotal(res.data?.count || 0);
      setPkColumns(res.data?.pk_columns || []);
      setEditableColumns(res.data?.editable_columns || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load rows');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTable, limit, offset]);

  useEffect(() => { loadTables(); }, [loadTables]);
  useEffect(() => { loadRows(); }, [loadRows]);

  // Secret columns may be omitted from rows; fall back to the table's declared columns
  const tableColumns = useMemo(() => {
    if (rows.length > 0) return Object.keys(rows[0]);
    const table = tables.find((t) => t.name === selectedTable);
    return table?.columns || [];
  }, [rows, tables, selectedTable]);

  const openEdit = (row) => {
    setEditRow(row);
    const editable = {};
    editableColumns.forEach((col) => {
      // Omitted or masked columns are never offered for editing
      if (col in row && row[col] !== MASK) editable[col] = row[col];
    });
    setEditPayload(JSON.stringify(editable, null, 2));
  };

  const saveEdit = async () => {
    if (!editRow) return;
    try {
      const parsed = JSON.parse(editPayload || '{}');
      // Never round-trip the mask back into the database
      const updates = {};
      Object.keys(parsed).forEach((key) => {
        if (parsed[key] !== MASK) updates[key] = parsed[key];
      });
      const pk = {};
      pkColumns.forEach((col) => {
        pk[col] = editRow[col];
      });
      await adminAPI.updateRow(selectedTable, pk, updates);
      setEditRow(null);
      setEditPayload('');
      loadRows();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to update row');
    }
  };

  const deleteRow = async (row) => {
    if (!pkColumns.length) return;
    const pkValue = row[pkColumns[0]];
    if (!(await confirm({
      title: 'Delete row',
      message: `Delete ${selectedTable} row ${pkValue}? This cannot be undone.`,
      confirmText: 'Delete'
    }))) return;
    try {
      await adminAPI.deleteRow(selectedTable, pkValue);
      loadRows();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to delete row');
    }
  };

  const setUserPassword = async (row) => {
    const newPassword = window.prompt('Enter new password (min 8 chars):');
    if (!newPassword) return;
    try {
      await adminAPI.setUserPassword(row.id, newPassword);
      alert('Password updated');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to update password');
    }
  };

  const isUsers = selectedTable === 'users';

  return (
    <div className="admin-page">
      {confirmDialog}
      <div className="admin-header">
        <h1>Tables</h1>
        <div className="admin-controls">
          <select
            value={selectedTable}
            onChange={(e) => {
              setSelectedTable(e.target.value);
              setOffset(0);
            }}
          >
            {tables.map((t) => (
              <option key={t.name} value={t.name}>{t.name}</option>
            ))}
          </select>
          <button onClick={loadRows} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="admin-warning">
        Direct database editing: changes are audited.
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-meta">
        <div>Rows: {total}</div>
        <div>PK: {pkColumns.join(', ') || '—'}</div>
      </div>

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              {tableColumns.map((col) => (
                <th key={col}>{col}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                {tableColumns.map((col) => (
                  <td key={col}>{cellText(row[col])}</td>
                ))}
                <td className="admin-actions">
                  <button onClick={() => openEdit(row)}>Edit</button>
                  {isUsers && (
                    <button onClick={() => setUserPassword(row)}>Set Password</button>
                  )}
                  {!isUsers && pkColumns.length === 1 && (
                    <button className="danger" onClick={() => deleteRow(row)}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={tableColumns.length + 1} className="admin-empty">
                  No rows found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="admin-pagination">
        <button
          onClick={() => setOffset(Math.max(0, offset - limit))}
          disabled={offset === 0}
        >
          Prev
        </button>
        <span>
          {total === 0 ? 0 : offset + 1}–{Math.min(offset + limit, total)} of {total}
        </span>
        <button
          onClick={() => setOffset(offset + limit)}
          disabled={offset + limit >= total}
        >
          Next
        </button>
        <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}>
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>

      {editRow && (
        <div className="admin-modal-backdrop" onClick={() => setEditRow(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Row</h3>
            <p className="admin-hint">
              Editable columns only. PK is locked. Secret columns are hidden and cannot be edited here.
            </p>
            <textarea
              value={editPayload}
              onChange={(e) => setEditPayload(e.target.value)}
              rows={16}
            />
            <div className="admin-modal-actions">
              <button onClick={() => setEditRow(null)}>Cancel</button>
              <button className="primary" onClick={saveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTables;
